import type { Category } from '../types';
import { FACTS } from '../facts';

const FEE = `$${FACTS.deliveryFeeUsd}`;
const SET_ASIDE = `${FACTS.taxSetAsidePct.low}–${FACTS.taxSetAsidePct.high}%`;

export const setUpYourBusiness: Category = {
  slug: 'set-up-your-business',
  half: 'business',
  accent: '#343F5B',
  icon: 'flag',
  label: { en: "Set up your business", es: "Monta tu negocio" },
  blurb: {
    en: "Sole proprietor vs LLC, getting an EIN, a separate business bank account, and picking a name.",
    es: "Propietario único vs LLC, cómo obtener un EIN, una cuenta bancaria separada para el negocio y cómo elegir un nombre.",
  },
  guides: [
    {
      slug: 'sole-prop-vs-llc',
      irsLinks: ['w-9', 'schedule-c'],
      related: ['set-up-your-business/get-an-ein', 'taxes-101/w9-and-1099'],
      body: {
        en: {
          title: "Sole proprietor or LLC?",
          summary: "The two most common ways Runners set up, what each one changes, and what stays the same either way.",
          sections: [
            {
              heading: "You may already be a sole proprietor",
              paragraphs: [
                "If you deliver under your own name and haven't registered anything with your state, you're a sole proprietor right now. Your delivery profit goes on Schedule C, attached to your personal tax return.",
                "Plenty of Runners stay here for years.",
              ],
            },
            {
              heading: "What an LLC adds",
              paragraphs: [
                "An LLC is a company you register with your state. It costs a filing fee and usually an annual fee after that. In return:",
              ],
              bullets: [
                "It can separate business debts and claims from your personal assets. How much protection you get varies by state and by how carefully you keep the two apart.",
                "A single-member LLC is taxed exactly like a sole proprietorship unless you choose otherwise.",
                "Later, an LLC can elect S-corp taxation. That adds payroll and filing costs, so it usually only makes sense at higher profit levels.",
              ],
            },
            {
              heading: "What stays the same",
              bullets: [
                "You're an independent contractor with Grocery List Plus Dispatch either way.",
                `You keep 100% of the ${FEE} delivery fee and 100% of your tips.`,
                "The lane pass is the same for everyone.",
                "You still give Grocery List Plus a W-9. The box you check tells us whether a 1099-NEC is sent to you.",
              ],
            },
            {
              heading: "How to choose",
              paragraphs: [
                "We won't tell you which to pick, and neither should a website. Weigh the state fees, how much liability separation matters to you, and how much paperwork you'll keep up with. Then confirm with an accountant before you file anything.",
              ],
            },
          ],
          actions: [
            "Write down what an LLC costs to form and maintain in your state.",
            "List what you personally own that you'd want kept separate from the business.",
            "Book 30 minutes with an accountant and bring both lists.",
          ],
        },
        es: {
          title: "¿Propietario único o LLC?",
          summary: "Las dos formas más comunes en que los Runners se organizan, qué cambia cada una y qué sigue igual de cualquier manera.",
          sections: [
            {
              heading: "Puede que ya seas propietario único",
              paragraphs: [
                "Si haces entregas a tu propio nombre y no has registrado nada en tu estado, ya eres propietario único. Tu ganancia por entregas va en el Anexo C, adjunto a tu declaración personal de impuestos.",
                "Muchos Runners se quedan así durante años.",
              ],
            },
            {
              heading: "Qué aporta una LLC",
              paragraphs: [
                "Una LLC es una compañía que registras en tu estado. Cuesta una tarifa de registro y, por lo general, una tarifa anual después. A cambio:",
              ],
              bullets: [
                "Puede separar las deudas y reclamos del negocio de tus bienes personales. Cuánta protección obtienes varía según el estado y según qué tan bien mantengas separadas ambas cosas.",
                "Una LLC de un solo miembro paga impuestos exactamente igual que un propietario único, a menos que elijas otra cosa.",
                "Más adelante, una LLC puede optar por tributar como S-corp. Eso agrega costos de nómina y de presentación, así que normalmente solo tiene sentido con ganancias más altas.",
              ],
            },
            {
              heading: "Qué sigue igual",
              bullets: [
                "De cualquier forma eres un contratista independiente con Lista de Compras Plus Dispatch.",
                `Te quedas con el 100% de la tarifa de entrega de ${FEE} y el 100% de tus propinas.`,
                "El lane pass es el mismo para todos.",
                "Igual le entregas un W-9 a Lista de Compras Plus. La casilla que marques nos indica si se te envía un 1099-NEC.",
              ],
            },
            {
              heading: "Cómo elegir",
              paragraphs: [
                "No te diremos cuál escoger, y ningún sitio web debería hacerlo. Sopesa las tarifas de tu estado, cuánto te importa separar la responsabilidad y cuánto papeleo vas a mantener al día. Luego confírmalo con un contador antes de presentar nada.",
              ],
            },
          ],
          actions: [
            "Anota cuánto cuesta formar y mantener una LLC en tu estado.",
            "Haz una lista de lo que posees personalmente y que quisieras mantener separado del negocio.",
            "Agenda 30 minutos con un contador y lleva ambas listas.",
          ],
        },
      },
    },
    {
      slug: 'get-an-ein',
      irsLinks: ['ss-4', 'w-9'],
      related: ['set-up-your-business/business-bank-account', 'taxes-101/w9-and-1099'],
      body: {
        en: {
          title: "Getting an EIN",
          summary: "An Employer Identification Number is free, takes minutes, and lets you keep your Social Security number off your paperwork.",
          sections: [
            {
              heading: "What it is",
              paragraphs: [
                "An EIN is a nine-digit number the IRS assigns to a business. Think of it as a Social Security number for your delivery work. You don't need employees to get one, and you don't need an LLC. Sole proprietors can have one too.",
              ],
            },
            {
              heading: "Why a Runner might want one",
              bullets: [
                "You can put the EIN on your W-9 instead of your Social Security number, so fewer people ever see your SSN.",
                "Most banks ask for one to open a business checking account.",
                "If you form an LLC, you'll generally need one anyway.",
                "It makes you look, and feel, like the business you already are.",
              ],
            },
            {
              heading: "How to get it",
              paragraphs: [
                "Apply directly on IRS.gov. It's free and you usually get the number on the spot. You'll need your own SSN or ITIN, your business name (your own name is fine), and your address.",
                "Skip any site that charges a fee to 'file' it for you. The IRS never charges for an EIN.",
              ],
            },
            {
              heading: "One thing to remember",
              paragraphs: [
                "An EIN doesn't change how you're taxed. A sole proprietor with an EIN still files Schedule C. And if you ever change your entity type, say from sole proprietor to LLC, check whether you need a new EIN. An accountant can tell you in a minute.",
              ],
            },
          ],
          actions: [
            "Apply for your EIN on IRS.gov during business hours (the online tool has limited hours).",
            "Save the confirmation letter as a PDF somewhere you'll find it again.",
            "If you'd rather the EIN appear on your W-9 instead of your SSN, contact support to submit an updated W-9.",
          ],
        },
        es: {
          title: "Cómo obtener un EIN",
          summary: "Un Número de Identificación del Empleador es gratis, toma minutos y te permite dejar tu número de Seguro Social fuera de tu papeleo.",
          sections: [
            {
              heading: "Qué es",
              paragraphs: [
                "Un EIN es un número de nueve dígitos que el IRS le asigna a un negocio. Piénsalo como un número de Seguro Social para tu trabajo de entregas. No necesitas empleados para obtenerlo, ni necesitas una LLC. Los propietarios únicos también pueden tener uno.",
              ],
            },
            {
              heading: "Por qué un Runner podría querer uno",
              bullets: [
                "Puedes poner el EIN en tu W-9 en lugar de tu número de Seguro Social, así menos personas llegan a ver tu SSN.",
                "La mayoría de los bancos lo piden para abrir una cuenta de cheques de negocio.",
                "Si formas una LLC, por lo general lo necesitarás de todos modos.",
                "Hace que te veas, y te sientas, como el negocio que ya eres.",
              ],
            },
            {
              heading: "Cómo obtenerlo",
              paragraphs: [
                "Solicítalo directamente en IRS.gov. Es gratis y normalmente recibes el número al instante. Necesitarás tu propio SSN o ITIN, el nombre de tu negocio (tu propio nombre sirve) y tu dirección.",
                "Evita cualquier sitio que cobre una tarifa por 'tramitarlo' por ti. El IRS nunca cobra por un EIN.",
              ],
            },
            {
              heading: "Algo para recordar",
              paragraphs: [
                "Un EIN no cambia cómo pagas impuestos. Un propietario único con EIN sigue presentando el Anexo C. Y si algún día cambias tu tipo de entidad, digamos de propietario único a LLC, verifica si necesitas un EIN nuevo. Un contador te lo puede decir en un minuto.",
              ],
            },
          ],
          actions: [
            "Solicita tu EIN en IRS.gov en horario laboral (la herramienta en línea tiene horario limitado).",
            "Guarda la carta de confirmación como PDF en un lugar donde la vuelvas a encontrar.",
            "Si prefieres que en tu W-9 aparezca el EIN en lugar de tu SSN, entrega un W-9 actualizado a Lista de Compras Plus.",
          ],
        },
      },
    },
    {
      slug: 'business-bank-account',
      related: ['recordkeeping-and-money/set-aside-for-taxes', 'recordkeeping-and-money/receipts-and-bookkeeping', 'set-up-your-business/get-an-ein'],
      body: {
        en: {
          title: "A separate business bank account",
          summary: "One account for delivery money in and business costs out makes taxes, bookkeeping, and any LLC protection much cleaner.",
          sections: [
            {
              heading: "Why bother",
              paragraphs: [
                "Your weekly payout from Grocery List Plus Dispatch arrives by Stripe Connect. If it lands in the same account as your rent and groceries, every tax-time question starts with 'wait, what was that?'",
              ],
              bullets: [
                "Every deposit is delivery income. Every debit is a candidate deduction.",
                "Your bookkeeping app can pull the whole year in one click.",
                "If you have an LLC, keeping money separate is a big part of keeping its protection.",
              ],
            },
            {
              heading: "What to open",
              paragraphs: [
                "A basic business checking account is enough. Many banks and online banks offer them with no monthly fee if you keep a small balance or set up a direct deposit. Bring your EIN (or SSN if you're a sole proprietor without one), your ID, and any LLC paperwork.",
                "A sole proprietor can often open one under their own name plus a 'doing business as' name. Ask the bank what they need.",
              ],
            },
            {
              heading: "Set it up so it runs itself",
              bullets: [
                "Point your Stripe Connect payout at the new account.",
                "Get a debit card on it and use that card for fuel, bags, phone mounts, and other business buys.",
                `Move your tax set-aside (${SET_ASIDE} of profit) to a separate savings account each payout.`,
                "Pay yourself by transferring to your personal account. That transfer isn't an expense, so don't try to deduct it.",
              ],
            },
            {
              heading: "What never goes through it",
              paragraphs: [
                "Groceries for your customers. At chain-store stops you pay with a Grocery List Plus-funded virtual card, so your own money never touches the order. Keep it that way.",
              ],
            },
          ],
          actions: [
            "Open the account this week, even if it's online only.",
            "Ask support to update your payout bank account when it changes.",
            "Open a second savings account for your tax set-aside.",
          ],
        },
        es: {
          title: "Una cuenta bancaria separada para el negocio",
          summary: "Una sola cuenta donde entra el dinero de las entregas y salen los costos del negocio hace que los impuestos, la contabilidad y cualquier protección de una LLC sean mucho más claros.",
          sections: [
            {
              heading: "Por qué vale la pena",
              paragraphs: [
                "Tu pago semanal de Lista de Compras Plus Dispatch llega por Stripe Connect. Si cae en la misma cuenta que tu renta y tu despensa, cada pregunta en temporada de impuestos empieza con '¿espera, y eso qué era?'.",
              ],
              bullets: [
                "Cada depósito es ingreso por entregas. Cada cargo es una posible deducción.",
                "Tu app de contabilidad puede importar todo el año con un clic.",
                "Si tienes una LLC, mantener el dinero separado es una parte importante de conservar su protección.",
              ],
            },
            {
              heading: "Qué abrir",
              paragraphs: [
                "Una cuenta de cheques de negocio básica es suficiente. Muchos bancos y bancos en línea las ofrecen sin cuota mensual si mantienes un saldo pequeño o configuras un depósito directo. Lleva tu EIN (o tu SSN si eres propietario único sin EIN), tu identificación y cualquier documento de la LLC.",
                "Un propietario único muchas veces puede abrirla a su propio nombre más un nombre comercial ('doing business as'). Pregunta al banco qué necesita.",
              ],
            },
            {
              heading: "Configúrala para que funcione sola",
              bullets: [
                "Dirige tu pago de Stripe Connect a la cuenta nueva.",
                "Pide una tarjeta de débito y úsala para gasolina, bolsas, soportes para el teléfono y otras compras del negocio.",
                `Pasa tu apartado para impuestos (${SET_ASIDE} de la ganancia) a una cuenta de ahorros aparte en cada pago.`,
                "Págate a ti mismo transfiriendo a tu cuenta personal. Esa transferencia no es un gasto, así que no intentes deducirla.",
              ],
            },
            {
              heading: "Qué nunca pasa por ahí",
              paragraphs: [
                "La despensa de tus clientes. En las paradas en tiendas de cadena pagas con una tarjeta virtual financiada por Lista de Compras Plus, así que tu propio dinero nunca toca el pedido. Que siga así.",
              ],
            },
          ],
          actions: [
            "Abre la cuenta esta semana, aunque sea solo en línea.",
            "Avísale a soporte cuando cambie tu cuenta bancaria de pagos.",
            "Abre una segunda cuenta de ahorros para tu apartado de impuestos.",
          ],
        },
      },
    },
    {
      slug: 'naming-your-business',
      irsLinks: ['w-9'],
      related: ['set-up-your-business/sole-prop-vs-llc', 'set-up-your-business/business-bank-account'],
      body: {
        en: {
          title: "Naming your business",
          summary: "Your own name works fine; a business name is optional, cheap, and worth a few checks before you commit.",
          sections: [
            {
              heading: "You don't need a name to start",
              paragraphs: [
                "As a sole proprietor, your legal business name is your own name. That's what goes on your W-9 and your Schedule C. Plenty of Runners never go further, and that's completely fine.",
              ],
            },
            {
              heading: "When a name helps",
              bullets: [
                "You want a business bank account or debit card that doesn't show your personal name.",
                "You hand out a card or a thank-you note and want something memorable.",
                "You're forming an LLC, which requires a name registered with the state.",
              ],
            },
            {
              heading: "Checks before you commit",
              bullets: [
                "Search your state's business registry to make sure no one else has it.",
                "Do a quick trademark search on the USPTO site. Avoid anything that sounds like a big brand.",
                "Don't use 'Grocery List Plus', 'Grocery List Plus Dispatch', or 'Runner' in your name. Those belong to the service, and using them can confuse your customers about who they're hiring.",
                "Check that the web domain and social handle are free, even if you don't plan to use them yet.",
              ],
            },
            {
              heading: "Making it official",
              paragraphs: [
                "A sole proprietor usually files a 'doing business as' (DBA) with the county or state. It's inexpensive and lets banks accept payments under that name. An LLC registers its name as part of forming the company. Either way, if you take on a new name, tell Grocery List Plus by submitting an updated W-9 so your records match.",
              ],
            },
          ],
          actions: [
            "Decide whether you want a name at all this year. 'No' is a fine answer.",
            "If yes, run the checks above and write down what you find.",
            "Ask your county clerk or state website what a DBA costs.",
          ],
        },
        es: {
          title: "Cómo nombrar tu negocio",
          summary: "Tu propio nombre funciona bien; un nombre comercial es opcional, barato y merece unas cuantas verificaciones antes de decidirte.",
          sections: [
            {
              heading: "No necesitas un nombre para empezar",
              paragraphs: [
                "Como propietario único, el nombre legal de tu negocio es tu propio nombre. Eso es lo que va en tu W-9 y en tu Anexo C. Muchos Runners nunca van más allá, y está perfectamente bien.",
              ],
            },
            {
              heading: "Cuándo ayuda un nombre",
              bullets: [
                "Quieres una cuenta bancaria o tarjeta de débito de negocio que no muestre tu nombre personal.",
                "Entregas una tarjeta o una nota de agradecimiento y quieres algo memorable.",
                "Vas a formar una LLC, que requiere un nombre registrado en el estado.",
              ],
            },
            {
              heading: "Verificaciones antes de decidirte",
              bullets: [
                "Busca en el registro de negocios de tu estado para asegurarte de que nadie más lo tenga.",
                "Haz una búsqueda rápida de marcas en el sitio de la USPTO. Evita cualquier cosa que suene a una marca grande.",
                "No uses 'Lista de Compras Plus', 'Lista de Compras Plus Dispatch' ni 'Runner' en tu nombre. Pertenecen al servicio, y usarlos puede confundir a tus clientes sobre a quién están contratando.",
                "Revisa que el dominio web y el usuario en redes estén libres, aunque no planees usarlos todavía.",
              ],
            },
            {
              heading: "Hacerlo oficial",
              paragraphs: [
                "Un propietario único normalmente registra un 'doing business as' (DBA) en el condado o el estado. Es económico y permite que los bancos acepten pagos con ese nombre. Una LLC registra su nombre como parte de la formación de la compañía. En cualquier caso, si adoptas un nombre nuevo, avísale a Lista de Compras Plus entregando un W-9 actualizado para que tus registros coincidan.",
              ],
            },
          ],
          actions: [
            "Decide si quieres un nombre este año. 'No' es una respuesta válida.",
            "Si es que sí, haz las verificaciones de arriba y anota lo que encuentres.",
            "Pregunta en la oficina del condado o en el sitio web del estado cuánto cuesta un DBA.",
          ],
        },
      },
    },
  ],
};
